import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { useSelector } from 'react-redux';
import { getRouteMetadata, getSafeUserProperties, shouldTrack, trackError } from '../utils/postHogUtils';

export const usePostHogTracking = () => {
  const posthog = usePostHog();
  const location = useLocation();
  const { isAuthenticated, user } = useSelector(state => state.auth);

  // Track page views with enhanced metadata
  useEffect(() => {
    if (posthog && shouldTrack()) {
      try {
        const routeData = getRouteMetadata(location.pathname);
        
        posthog.capture('$pageview', {
          $current_url: window.location.href,
          $pathname: location.pathname,
          $search: location.search,
          route_name: routeData.name,
          route_category: routeData.category,
          is_authenticated: isAuthenticated,
          user_id: user?._id || user?.id || null,
          timestamp: new Date().toISOString(),
        });
      } catch (error) {
        console.error('PostHog: Failed to track pageview', error);
        trackError('posthog_pageview_failed', {
          pathname: location.pathname,
          error_message: error.message,
        });
      }
    }
  }, [location.pathname, location.search, posthog, isAuthenticated, user]);

useEffect(() => {
  if (posthog && shouldTrack() && isAuthenticated && user) {
    try {
      const userProperties = getSafeUserProperties(user);
      
      posthog.setPersonProperties({
        ...userProperties,
        last_activity: new Date().toISOString(),
      });
    } catch (error) {
      console.error('PostHog: Failed to update user properties', error);
      trackError('posthog_update_properties_failed', {
        user_id: user?._id || user?.id,
        error_message: error.message,
      });
    }
  }
}, [posthog, isAuthenticated, user?._id, user?.credits, user?.profileCompletionPercentage]);

  // Reset PostHog when user logs out
  useEffect(() => {
    if (posthog && shouldTrack() && !isAuthenticated) {
      try {
        posthog.reset();
      } catch (error) {
        console.error('PostHog: Failed to reset on logout', error);
        trackError('posthog_reset_failed', {
          error_message: error.message,
        });
      }
    }
  }, [posthog, isAuthenticated]);

  // Track user session updates (credits, profile completion, etc.)
  useEffect(() => {
    if (posthog && shouldTrack() && isAuthenticated && user) {
      try {
        const userProperties = getSafeUserProperties(user);
        
        posthog.setPersonProperties({
          ...userProperties,
          last_activity: new Date().toISOString(),
        });
      } catch (error) {
        console.error('PostHog: Failed to update user properties', error);
        trackError('posthog_update_properties_failed', {
          user_id: user?._id || user?.id,
          error_message: error.message,
        });
      }
    }
  }, [posthog, user?.credits, user?.profileCompletionPercentage, isAuthenticated]);

  return {
    posthog,
    isTracking: shouldTrack() && !!posthog,
  };
};
